// Exportar para que sea considerado un módulo
export {};

export interface CloudinaryUploadResult {
  public_id: string;
  secure_url: string;
  format: string;
  bytes: number;
  width: number;
  height: number;
  resource_type: string;
  created_at: string;
}

export interface CloudinaryOptions {
  folder?: string;
  resource_type?: 'image' | 'video' | 'auto';
  quality?: 'auto' | 'best' | 'good' | 'eco' | 'low';
  crop?: 'scale' | 'fit' | 'limit' | 'mfit' | 'fill' | 'lfill' | 'pad' | 'lpad' | 'mpad' | 'crop' | 'thumb';
  width?: number;
  height?: number;
}

/**
 * Sube una imagen a Cloudinary usando fetch directo
 * @param file - Archivo de imagen a subir
 * @param options - Opciones de subida opcionales
 * @returns Promise con el resultado de la subida
 */
export const uploadImage = async (
  file: File,
  options: CloudinaryOptions = {}
): Promise<CloudinaryUploadResult> => {
  try {
    const defaultOptions: CloudinaryOptions = {
      folder: 'atomic-corporation/products',
      resource_type: 'image',
      quality: 'auto',
      crop: 'limit',
      width: 1200,
      height: 1200
    };

    const uploadOptions = { ...defaultOptions, ...options };

    // Para subir desde el cliente, necesitamos usar upload preset sin firmar
    // Esto requiere configurar un upload preset en la consola de Cloudinary
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'atomic_products'); // Upload preset configurado en Cloudinary
    formData.append('folder', uploadOptions.folder || 'atomic-corporation/products');
    
    if (uploadOptions.quality) {
      formData.append('quality', uploadOptions.quality);
    }
    
    if (uploadOptions.crop) {
      formData.append('crop', uploadOptions.crop);
    }
    
    if (uploadOptions.width) {
      formData.append('width', uploadOptions.width.toString());
    }
    
    if (uploadOptions.height) {
      formData.append('height', uploadOptions.height.toString());
    }

    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
    
    if (!cloudName) {
      throw new Error('El nombre de cloud de Cloudinary no está configurado. Verifica las variables de entorno.');
    }
    
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
      {
        method: 'POST',
        body: formData
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Error al subir imagen: ${errorData.error?.message || 'Error desconocido'}`);
    }

    const result = await response.json();
    
    return {
      public_id: result.public_id,
      secure_url: result.secure_url,
      format: result.format,
      bytes: result.bytes,
      width: result.width,
      height: result.height,
      resource_type: result.resource_type,
      created_at: result.created_at
    };
  } catch (error) {
    console.error('Error en uploadImage:', error);
    throw error;
  }
};

/**
 * Elimina una imagen de Cloudinary
 * @param publicId - ID público de la imagen a eliminar
 * @returns Promise con el resultado de la eliminación
 */
export const deleteImage = async (publicId: string): Promise<{ result: string }> => {
  try {
    // Nota: Esta función debe ser ejecutada en el backend por seguridad
    // Las claves API no deben exponerse en el cliente
    const response = await fetch('/api/cloudinary/delete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ publicId }),
    });

    if (!response.ok) {
      throw new Error('Error al eliminar imagen');
    }

    return await response.json();
  } catch (error) {
    console.error('Error en deleteImage:', error);
    throw error;
  }
};

/**
 * Genera una URL optimizada para una imagen de Cloudinary
 * @param publicId - ID público de la imagen
 * @param options - Opciones de transformación
 * @returns URL optimizada
 */
export const getOptimizedUrl = (
  publicId: string,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    crop?: string;
    format?: string;
  } = {}
): string => {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const transformations = [];

  if (options.width) transformations.push(`w_${options.width}`);
  if (options.height) transformations.push(`h_${options.height}`);
  if (options.quality) transformations.push(`q_${options.quality}`);
  if (options.crop) transformations.push(`c_${options.crop}`);
  if (options.format) transformations.push(`f_${options.format}`);

  const transformationString = transformations.length > 0 ? transformations.join(',') : 'q_auto,f_auto';
  
  return `https://res.cloudinary.com/${cloudName}/image/upload/${transformationString}/${publicId}`;
};

/**
 * Genera una URL pública para acceder a un documento de Cloudinary sin autenticación
 * @param url - URL segura del documento
 * @returns URL pública para acceso directo
 */
export const getPublicUrl = (url: string): string => {
  // Si la URL ya contiene parámetros, los mantenemos
  if (url.includes('?')) {
    return `${url}&_a=${Date.now()}`;
  }
  
  // Agregamos un parámetro de timestamp para evitar caché y forzar acceso público
  return `${url}?_a=${Date.now()}`;
};

/**
 * Determina si una URL es de Cloudinary y ajusta el acceso para PDFs
 * @param url - URL del documento
 * @returns URL ajustada para acceso
 */
export const adjustUrlForDocumentAccess = (url: string): string => {
  if (!url) return url;
  
  // Para archivos de Cloudinary, agregamos parámetros para evitar problemas de caché y CORS
  if (url.includes('cloudinary.com')) {
    // Para PDFs, mantenemos la URL original pero agregamos parámetros para mejor acceso
    if (url.toLowerCase().includes('.pdf')) {
      // Agregamos parámetros para forzar la descarga y evitar problemas de caché
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}_a=${Date.now()}#toolbar=0&navpanes=0&scrollbar=0`;
    }
    
    // Para otros archivos, solo agregamos timestamp para evitar caché
    return url.includes('?') ? `${url}&_a=${Date.now()}` : `${url}?_a=${Date.now()}`;
  }
  
  return url;
};